import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import socketIoConnection from '../../../../socketIoConnect';
import { useAuth } from '../../../../context/auth-context';

const STORAGE_KEY = 'bunmail_drafts';
const EXPIRY_DAYS = 7;

const DraftContext = createContext(null);

/** Load drafts from localStorage, filtering out expired ones. */
const loadDrafts = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return parsed.filter((d) => d.created_at >= cutoff);
  } catch {
    return [];
  }
};

/** Generate a short random ID for browser drafts. */
const generateId = () => `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// App and the advisor shell both mount a provider; a nested one would open a
// second socket and save every pushed draft twice.
export const DraftProvider = ({ children }) =>
  useContext(DraftContext) ? children : <DraftStore>{children}</DraftStore>;

const DraftStore = ({ children }) => {
  const queryClient = useQueryClient();
  const accessToken = useAuth()?.accessToken;
  const [drafts, setDrafts] = useState(loadDrafts);
  const draftsRef = useRef(drafts);
  const saveTimerRef = useRef(null);

  // Socket requests can arrive before React renders the preceding local edit.
  const changeDrafts = useCallback((update) => {
    const next = update(draftsRef.current);
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  // Debounced localStorage write (300ms)
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    }, 300);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [drafts]);

  const createDraft = useCallback((initialData = {}) => {
    const newDraft = {
      id: generateId(),
      to_email: '',
      cc_email: '',
      bcc_email: '',
      subject: '',
      content: null,
      in_reply_to: null,
      thread_id: null,
      client_household_id: null,
      attachments: [], // [{document_id, name, mime_type, file_path}]
      is_minimized: false,
      created_at: Date.now(),
      ...initialData,
    };
    changeDrafts((prev) => [newDraft, ...prev]);
    return newDraft;
  }, [changeDrafts]);

  const updateDraft = useCallback((id, updates) => {
    changeDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...updates } : d))
    );
  }, [changeDrafts]);

  const deleteDraft = useCallback((id) => {
    changeDrafts((prev) => prev.filter((d) => d.id !== id));
  }, [changeDrafts]);

  const getDraft = useCallback(
    (id) => drafts.find((d) => d.id === id) || null,
    [drafts]
  );

  // Listen for server-pushed drafts (from Parabot tools, smart table automations, etc.)
  // and for permanent send failures surfaced by the send queue's dead-letter path.
  useEffect(() => {
    const socket = socketIoConnection('bunmail');
    if (!socket) return;

    const handleDraftCreated = (data, ack) => {
      try {
        createDraft(data);
        if (typeof ack === 'function') ack({ ok: true });
      } catch (e) {
        if (typeof ack === 'function') ack({ ok: false, error: e?.message || String(e) });
      }
    };

    const handleDraftList = (_data, ack) => {
      if (typeof ack !== 'function') return;
      ack({ ok: true, drafts: draftsRef.current.map((draft) => ({
        id: draft.id,
        to_email: draft.to_email,
        cc_email: draft.cc_email,
        bcc_email: draft.bcc_email,
        subject: draft.subject,
        content: draft.content,
        is_minimized: draft.is_minimized,
      })) });
    };

    const handleDraftUpdate = ({ draft_id: draftId, updates = {} }, ack) => {
      const respond = (result) => { if (typeof ack === 'function') ack(result); };
      const draft = draftsRef.current.find((item) => item.id === draftId);
      if (!draft) {
        respond({ ok: false, status: 'not_found' });
        return;
      }
      const patch = {};
      ['to_email', 'cc_email', 'bcc_email', 'subject', 'content'].forEach((field) => {
        if (typeof updates[field] === 'string') patch[field] = updates[field];
      });
      // Remount open body editors only for external body replacements.
      if (Object.prototype.hasOwnProperty.call(patch, 'content')) {
        patch.parabot_content_revision = (draft.parabot_content_revision || 0) + 1;
      }
      const next = draftsRef.current.map((item) => item.id === draftId ? { ...item, ...patch } : item);
      try {
        // Confirm only after persistence succeeds, keeping failed edits out of state.
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        changeDrafts(() => next);
        respond({ ok: true });
      } catch (_error) {
        respond({ ok: false, status: 'save_failed' });
      }
    };

    const handleSendFailed = ({ to, subject, error }) => {
      const recipient = to ? ` to ${to}` : '';
      const label = subject ? ` "${subject}"` : '';
      // eslint-disable-next-line no-alert
      window.alert(
        `Your email${label}${recipient} could not be sent.\n\n${error || 'Unknown error'}\n\nPlease try again or reduce the attachment size.`
      );
    };

    const handleJourneyFollowupSent = async ({ prospectId, advanced } = {}) => {
      if (process.env.NODE_ENV !== 'test') {
        console.info('[CLIENT-JOURNEY-DRAFT] browser_refresh_received', {
          prospect_id: prospectId || null,
          advanced: Boolean(advanced),
        });
      }
      try {
        await Promise.all([
          queryClient.invalidateQueries(['client-journey']),
          queryClient.invalidateQueries(['client-journey-followups']),
        ]);
        if (process.env.NODE_ENV !== 'test') {
          console.info('[CLIENT-JOURNEY-DRAFT] browser_refresh_completed', {
            prospect_id: prospectId || null,
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'test') {
          console.error('[CLIENT-JOURNEY-DRAFT] browser_refresh_failed', {
            prospect_id: prospectId || null,
            error_message: error.message,
          });
        }
      }
    };

    socket.on('draft:created', handleDraftCreated);
    socket.on('draft:list', handleDraftList);
    socket.on('draft:update', handleDraftUpdate);
    socket.on('email:send_failed', handleSendFailed);
    socket.on('journey:followup_sent', handleJourneyFollowupSent);
    return () => {
      socket.off('draft:created', handleDraftCreated);
      socket.off('draft:list', handleDraftList);
      socket.off('draft:update', handleDraftUpdate);
      socket.off('email:send_failed', handleSendFailed);
      socket.off('journey:followup_sent', handleJourneyFollowupSent);
    };
  }, [createDraft, changeDrafts, queryClient, accessToken]);

  return (
    <DraftContext.Provider value={{ drafts, createDraft, updateDraft, deleteDraft, getDraft }}>
      {children}
    </DraftContext.Provider>
  );
};

export const useDrafts = () => {
  const ctx = useContext(DraftContext);
  if (!ctx) throw new Error('useDrafts must be used within a DraftProvider');
  return ctx;
};

export default DraftContext;
